import * as React from 'react';
import { FileText, Globe, Save, X } from 'lucide-react';
import apiService from '../../services/api';
import './AnalyzeAgainModal.css';
import './ClientRequestPreferencesModal.css';

export const CLIENT_REQUEST_SOURCES = {
  THEIR_SIDE_REDLINES: 'their_side_redlines',
  OUR_SIDE_REDLINES: 'our_side_redlines',
  PLAIN_TEXT: 'plain_text',
} as const;

export type ClientRequestSource =
  (typeof CLIENT_REQUEST_SOURCES)[keyof typeof CLIENT_REQUEST_SOURCES];

const SOURCE_OPTIONS: { value: ClientRequestSource; label: string }[] = [
  { value: CLIENT_REQUEST_SOURCES.THEIR_SIDE_REDLINES, label: 'Their side redlines' },
  { value: CLIENT_REQUEST_SOURCES.OUR_SIDE_REDLINES, label: 'Our side redlines' },
  { value: CLIENT_REQUEST_SOURCES.PLAIN_TEXT, label: 'Plain text' },
];

interface ClientRequestPreferencesModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialModuleA: string[];
  initialModuleB: string[];
  onSaved?: () => void | Promise<void>;
}

export const ClientRequestPreferencesModal: React.FC<ClientRequestPreferencesModalProps> = ({
  isOpen,
  onClose,
  initialModuleA,
  initialModuleB,
  onSaved,
}) => {
  const [moduleA] = React.useState<string[]>(
    initialModuleA.length ? initialModuleA : [CLIENT_REQUEST_SOURCES.PLAIN_TEXT],
  );
  const [moduleB, setModuleB] = React.useState<string[]>(initialModuleB);
  const [isSaving, setIsSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (isOpen) {
      setModuleB(
        initialModuleB.length ? initialModuleB : [CLIENT_REQUEST_SOURCES.THEIR_SIDE_REDLINES],
      );
      setError(null);
    }
  }, [isOpen, initialModuleB]);

  if (!isOpen) return null;

  const toggleModuleB = (value: ClientRequestSource) => {
    setModuleB((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  };

  const handleSave = async () => {
    if (moduleB.length === 0) {
      setError('Select at least one source for Module B.');
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      await apiService.updateClientRequestPreferences(undefined, moduleB);
      if (onSaved) await onSaved();
      onClose();
    } catch (e: any) {
      setError(
        e?.response?.data?.detail || e?.message || 'Failed to save preferences. Please try again.',
      );
    } finally {
      setIsSaving(false);
    }
  };

  const renderGroup = (
    title: string,
    icon: React.ReactNode,
    selected: string[],
    disabled: boolean,
    onToggle?: (value: ClientRequestSource) => void,
  ) => (
    <div className={`cr-prefs-group ${disabled ? 'cr-prefs-group-disabled' : ''}`}>
      <div className="cr-prefs-group-header">
        {icon}
        <span className="cr-prefs-group-title">{title}</span>
        {disabled && <span className="cr-prefs-group-badge">Coming soon</span>}
      </div>
      <div className="cr-prefs-options">
        {SOURCE_OPTIONS.map((option) => {
          const checked = selected.includes(option.value);
          return (
            <label
              key={option.value}
              className={`cr-prefs-option ${disabled ? 'cr-prefs-option-disabled' : ''}`}
            >
              <input
                type="checkbox"
                checked={checked}
                disabled={disabled}
                onChange={() => onToggle && onToggle(option.value)}
              />
              <span>{option.label}</span>
            </label>
          );
        })}
      </div>
    </div>
  );

  return (
    <div
      className="aa-modal-backdrop cr-prefs-modal-backdrop"
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget && !isSaving) onClose();
      }}
    >
      <div
        className="aa-modal cr-prefs-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cr-prefs-modal-title"
      >
        <div className="aa-modal-header">
          <div className="aa-modal-header-text">
            <h2 id="cr-prefs-modal-title" className="aa-modal-title">
              Client Request Sources
            </h2>
            <p className="aa-modal-subtitle">Choose which sources count as client requests</p>
          </div>
          <button
            type="button"
            className="aa-modal-close"
            onClick={onClose}
            disabled={isSaving}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <p className="aa-modal-lead">
          Select one or more sources for Module B cross-checks. Module A (Web) is locked to plain
          text for now.
        </p>

        {error && <div className="cr-prefs-error">{error}</div>}

        <div className="cr-prefs-groups">
          {renderGroup(
            'Module A - Web',
            <Globe size={16} className="cr-prefs-group-icon cr-prefs-group-icon-muted" />,
            moduleA,
            true,
          )}
          {renderGroup(
            'Module B - Word',
            <FileText size={16} className="cr-prefs-group-icon" />,
            moduleB,
            false,
            toggleModuleB,
          )}
        </div>

        <div className="aa-modal-actions cr-prefs-actions">
          <button
            type="button"
            className="aa-btn-secondary"
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="aa-btn-primary"
            onClick={handleSave}
            disabled={isSaving}
          >
            <Save size={14} aria-hidden />
            {isSaving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClientRequestPreferencesModal;
